import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
  Mail, Calendar, Clock, Send, Settings, Check,
  TrendingUp, TrendingDown, DollarSign, AlertTriangle,
  FileText, Eye, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getDemoDigestPreview = () => ({
  period_start: '2026-03-31',
  period_end: '2026-04-07',
  total_spend: 71240,
  change_percentage: -5.2,
  top_services: [
    { name: 'AWS EC2', provider: 'aws', cost: 23800, change: -3.1 },
    { name: 'AWS S3', provider: 'aws', cost: 10575, change: 1.2 },
    { name: 'AWS RDS', provider: 'aws', cost: 9675, change: -2.8 },
    { name: 'Azure VMs', provider: 'azure', cost: 8400, change: -8.4 },
    { name: 'GCP Compute', provider: 'gcp', cost: 6200, change: -1.5 },
  ],
  anomalies_count: 3,
  total_savings_opportunity: 8940,
});

const WeeklyDigest = () => {
  const { shouldShowData } = useDemoData();
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [activeTab, setActiveTab] = useState('settings');
  const [config, setConfig] = useState({
    enabled: true,
    frequency: 'weekly',
    day_of_week: 1,
    email: '',
    include_forecasts: true,
    include_anomalies: true,
    include_recommendations: true
  });
  const [template, setTemplate] = useState({
    companyName: 'CARTIEAI',
    headerColor: '#059669',
    greeting: 'Hi {name},',
    intro: "Here's your cloud cost summary for the past week.",
    footer: '© 2026 CARTIEAI | Founded by Lakshmi Kiranmai Guduru',
    ctaText: 'View Full Dashboard',
    ctaUrl: 'https://cartieai.com/dashboard'
  });

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [previewRes, configRes] = await Promise.all([
        axios.get(`${API_URL}/api/digest/preview?days=7`, { headers }),
        axios.get(`${API_URL}/api/digest/config`, { headers })
      ]);
      
      // Normalize the preview data format
      const rawPreview = previewRes.data;
      const normalizedPreview = {
        period_start: rawPreview.period?.start || rawPreview.period_start,
        period_end: rawPreview.period?.end || rawPreview.period_end,
        total_spend: rawPreview.summary?.this_week_total || rawPreview.total_spend || 0,
        change_percentage: rawPreview.summary?.change_percent || rawPreview.change_percentage || 0,
        top_services: (rawPreview.top_services || []).map(svc => ({
          name: svc.service || svc.name,
          provider: svc.provider || (svc.service?.includes('AWS') ? 'aws' : svc.service?.includes('Azure') ? 'azure' : svc.service?.includes('GCP') ? 'gcp' : 'snowflake'),
          cost: svc.cost,
          change: svc.change || 0
        })),
        anomalies_count: rawPreview.anomalies?.count || rawPreview.anomalies_count || 0,
        total_savings_opportunity: rawPreview.savings_opportunity?.total || rawPreview.total_savings_opportunity || 0,
        provider_breakdown: rawPreview.provider_breakdown || null
      };
      
      setPreview(normalizedPreview);
      
      // If all zeros and demo user, use demo data
      if (normalizedPreview.total_spend === 0 && shouldShowData) {
        setPreview(getDemoDigestPreview());
      }
      
      if (configRes.data) {
        setConfig(prev => ({ ...prev, ...configRes.data }));
      }
    } catch (error) {
      console.error('Failed to fetch digest data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSaveConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/digest/config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Digest settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleSendNow = async () => {
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/digest/send`,
        { recipients: [config.email || 'hello@cartieai.com'] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Handle response from quick_wins.py format
      if (response.data.email_status?.success || response.data.digest) {
        toast.success(`Digest sent to ${config.email || 'hello@cartieai.com'}!`);
      } else if (response.data.success) {
        toast.success(`Digest sent to ${response.data.email}!`);
      } else {
        toast.error(response.data.email_status?.message || 'Failed to send digest');
      }
    } catch (error) {
      toast.error('Failed to send digest');
    } finally {
      setSending(false);
    }
  };

  const formatCurrency = (value) => `$${value?.toLocaleString() || 0}`;

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  // Admin access check
  if (!isAdmin) {
    return (
      <div className="p-6" data-testid="weekly-digest">
        <Card className="border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800">
          <CardContent className="py-8 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-red-700 dark:text-red-400">Access Denied</h2>
            <p className="text-red-600 dark:text-red-300 mt-2">Weekly Digest management is only available to administrators.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="weekly-digest">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <Mail className="w-7 h-7 text-emerald-500" />
            Weekly Digest
          </h1>
          <p className="text-slate-400 dark:text-slate-400">
            Automated cost summary reports delivered to your inbox
          </p>
        </div>
        <Button onClick={handleSendNow} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700">
          <Send className={`w-4 h-4 mr-2 ${sending ? 'animate-pulse' : ''}`} />
          {sending ? 'Sending...' : 'Send Now'}
        </Button>
      </div>

      {/* Tabs for Settings/Template */}
      <div className="flex gap-2 border-b border-slate-200 dark:border-slate-700">
        <button
          onClick={() => setActiveTab('settings')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${
            activeTab === 'settings'
              ? 'text-emerald-600 border-b-2 border-emerald-600'
              : 'text-slate-400 hover:text-slate-700'
          }`}
        >
          <Settings className="w-4 h-4 inline mr-2" />
          Delivery Settings
        </button>
        <button
          onClick={() => setActiveTab('template')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${
            activeTab === 'template'
              ? 'text-emerald-600 border-b-2 border-emerald-600'
              : 'text-slate-400 hover:text-slate-700'
          }`}
        >
          <FileText className="w-4 h-4 inline mr-2" />
          Email Template
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel - Settings or Template based on tab */}
        {activeTab === 'settings' ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Digest Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Enable Weekly Digest</Label>
                <p className="text-sm text-slate-400">Receive automated cost reports</p>
              </div>
              <Switch
                checked={config.enabled}
                onCheckedChange={(checked) => setConfig(prev => ({ ...prev, enabled: checked }))}
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label>Email Address</Label>
              <Input
                type="email"
                placeholder="your@email.com"
                value={config.email}
                onChange={(e) => setConfig(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>

            {/* Frequency */}
            <div className="space-y-2">
              <Label>Frequency</Label>
              <select
                value={config.frequency}
                onChange={(e) => setConfig(prev => ({ ...prev, frequency: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            {/* Day of Week */}
            {config.frequency === 'weekly' && (
              <div className="space-y-2">
                <Label>Day of Week</Label>
                <select
                  value={config.day_of_week}
                  onChange={(e) => setConfig(prev => ({ ...prev, day_of_week: Number(e.target.value) }))}
                  className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800"
                >
                  {days.map((day, idx) => (
                    <option key={day} value={idx}>{day}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Include Options */}
            <div className="space-y-3 pt-4 border-t">
              <Label className="text-base">Include in Digest</Label>
              
              <div className="flex items-center justify-between">
                <span className="text-sm">Cost Forecasts</span>
                <Switch
                  checked={config.include_forecasts}
                  onCheckedChange={(checked) => setConfig(prev => ({ ...prev, include_forecasts: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm">Anomaly Alerts</span>
                <Switch
                  checked={config.include_anomalies}
                  onCheckedChange={(checked) => setConfig(prev => ({ ...prev, include_anomalies: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm">Savings Recommendations</span>
                <Switch
                  checked={config.include_recommendations}
                  onCheckedChange={(checked) => setConfig(prev => ({ ...prev, include_recommendations: checked }))}
                />
              </div>
            </div>

            <Button onClick={handleSaveConfig} className="w-full bg-emerald-600 hover:bg-emerald-700">
              <Check className="w-4 h-4 mr-2" />
              Save Settings
            </Button>
          </CardContent>
        </Card>
        ) : (
        /* Template Customization Card */
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Email Template
            </CardTitle>
            <CardDescription>Customize your digest email appearance</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Company Name */}
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input
                value={template.companyName}
                onChange={(e) => setTemplate(prev => ({ ...prev, companyName: e.target.value }))}
                placeholder="Your Company Name"
              />
            </div>

            {/* Header Color */}
            <div className="space-y-2">
              <Label>Header Color</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={template.headerColor}
                  onChange={(e) => setTemplate(prev => ({ ...prev, headerColor: e.target.value }))}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  value={template.headerColor}
                  onChange={(e) => setTemplate(prev => ({ ...prev, headerColor: e.target.value }))}
                  placeholder="#059669"
                  className="flex-1"
                />
              </div>
            </div>

            {/* Greeting */}
            <div className="space-y-2">
              <Label>Greeting (use {'{name}'} for recipient)</Label>
              <Input
                value={template.greeting}
                onChange={(e) => setTemplate(prev => ({ ...prev, greeting: e.target.value }))}
                placeholder="Hi {name},"
              />
            </div>

            {/* Intro Text */}
            <div className="space-y-2">
              <Label>Introduction Text</Label>
              <textarea
                value={template.intro}
                onChange={(e) => setTemplate(prev => ({ ...prev, intro: e.target.value }))}
                className="w-full px-3 py-2 border rounded-md bg-background text-sm min-h-[80px] resize-none"
                placeholder="Here's your weekly cost summary..."
              />
            </div>

            {/* CTA Button */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>CTA Button Text</Label>
                <Input
                  value={template.ctaText}
                  onChange={(e) => setTemplate(prev => ({ ...prev, ctaText: e.target.value }))}
                  placeholder="View Dashboard"
                />
              </div>
              <div className="space-y-2">
                <Label>CTA Button URL</Label>
                <Input
                  value={template.ctaUrl}
                  onChange={(e) => setTemplate(prev => ({ ...prev, ctaUrl: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
            </div>

            {/* Footer */}
            <div className="space-y-2">
              <Label>Footer Text</Label>
              <Input
                value={template.footer}
                onChange={(e) => setTemplate(prev => ({ ...prev, footer: e.target.value }))}
                placeholder="© 2026 Your Company"
              />
            </div>

            <Button onClick={() => toast.success('Template saved!')} className="w-full bg-emerald-600 hover:bg-emerald-700">
              <Check className="w-4 h-4 mr-2" />
              Save Template
            </Button>
          </CardContent>
        </Card>
        )}

        {/* Preview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              {activeTab === 'template' ? 'Email Preview' : 'Digest Preview'}
            </CardTitle>
            <CardDescription>
              {activeTab === 'template' 
                ? 'Preview how your email will look'
                : `${preview?.period_start} to ${preview?.period_end}`
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {activeTab === 'template' ? (
              /* Styled Email Preview */
              <div className="border rounded-lg overflow-hidden shadow-sm">
                {/* Email Header */}
                <div style={{ backgroundColor: template.headerColor }} className="p-4 text-white">
                  <h2 className="text-xl font-bold">{template.companyName}</h2>
                  <p className="text-sm opacity-90">Weekly Cost Digest</p>
                </div>
                
                {/* Email Body */}
                <div className="p-4 bg-white dark:bg-slate-800 space-y-4">
                  <p className="text-slate-700 dark:text-slate-300">
                    {template.greeting.replace('{name}', 'Team')}
                  </p>
                  <p className="text-slate-400 dark:text-slate-400 text-sm">
                    {template.intro}
                  </p>
                  
                  {/* Sample Content */}
                  <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Total Spend: $104,662.65</p>
                    <p className="text-xs text-emerald-600">↓ 2.04% vs last week</p>
                  </div>
                  
                  {/* CTA Button */}
                  <a 
                    href={template.ctaUrl}
                    style={{ backgroundColor: template.headerColor }}
                    className="inline-block px-6 py-2 text-white rounded-lg text-sm font-medium"
                  >
                    {template.ctaText}
                  </a>
                </div>
                
                {/* Email Footer */}
                <div className="p-3 bg-slate-100 dark:bg-slate-900 text-center">
                  <p className="text-xs text-slate-400">{template.footer}</p>
                </div>
              </div>
            ) : (
            <>
            {/* Total Spend */}
            <div className="p-4 bg-slate-800 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl text-white">
              <p className="text-sm opacity-80">Total Cloud Spend</p>
              <p className="text-3xl font-bold">{formatCurrency(preview?.total_spend)}</p>
              <div className="flex items-center gap-2 mt-2">
                <Badge className={preview?.change_percentage > 0 ? 'bg-red-500' : 'bg-emerald-500'}>
                  {preview?.change_percentage > 0 ? '↑' : '↓'} {Math.abs(preview?.change_percentage || 0)}%
                </Badge>
                <span className="text-sm opacity-70">vs last week</span>
              </div>
            </div>

            {/* Provider Breakdown */}
            <div>
              <h4 className="font-medium mb-3">By Provider</h4>
              <div className="space-y-2">
                {preview?.provider_breakdown && Object.entries(preview.provider_breakdown).map(([provider, cost]) => (
                  <div key={provider} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded">
                    <span className="font-medium">{provider.toUpperCase()}</span>
                    <span className="text-slate-400 dark:text-slate-300">{formatCurrency(cost)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Services */}
            <div>
              <h4 className="font-medium mb-3">Top Services</h4>
              <div className="space-y-2">
                {preview?.top_services?.slice(0, 3).map((svc, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded">
                    <div>
                      <span className="font-medium">{svc.name}</span>
                      {svc.provider && <span className="text-xs text-slate-400 ml-2">{svc.provider.toUpperCase()}</span>}
                    </div>
                    <div className="text-right">
                      <span className="font-medium">{formatCurrency(svc.cost)}</span>
                      {svc.change !== undefined && svc.change !== 0 && (
                        <span className={`text-xs ml-2 ${svc.change > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                          {svc.change > 0 ? '+' : ''}{svc.change}%
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Savings */}
            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-600 dark:text-emerald-400">Savings Opportunities</p>
                  <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                    {formatCurrency(preview?.total_savings_opportunity)}
                  </p>
                </div>
                <DollarSign className="w-10 h-10 text-emerald-500" />
              </div>
            </div>

            {/* Anomalies */}
            {preview?.anomalies_count > 0 && (
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  <span className="font-medium text-amber-700 dark:text-amber-300">
                    {preview.anomalies_count} anomalies detected
                  </span>
                </div>
              </div>
            )}
            </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WeeklyDigest;
